import type { BootItem, BootTag, BootTopThree } from './types';

export function itemsWithTag(items: BootItem[], tag: BootTag): BootItem[] {
  return items.filter((item) => item.tags.includes(tag));
}

export function sortByRankWithinTag(items: BootItem[], tag: BootTag): BootItem[] {
  return [...itemsWithTag(items, tag)].sort((a, b) => {
    const ar = a.rankWithinTag ?? 999;
    const br = b.rankWithinTag ?? 999;
    return ar - br;
  });
}

export function applyTagRanking(itemIds: string[], items: BootItem[], tag: BootTag): BootItem[] {
  const ranked = itemIds.map((id, index) => {
    const item = items.find((i) => i.id === id);
    if (!item) return null;
    return { ...item, rankWithinTag: index + 1 };
  }).filter(Boolean) as BootItem[];

  const rankedIds = new Set(itemIds);
  const others = items
    .filter((item) => item.tags.includes(tag) && !rankedIds.has(item.id))
    .map((item) => ({ ...item, rankWithinTag: undefined }));

  const untouched = items.filter((item) => !item.tags.includes(tag));

  return [...untouched, ...others, ...ranked];
}

export function deriveTopThree(items: BootItem[]): BootTopThree {
  const topA = sortByRankWithinTag(items, 'A')[0];
  const topT = sortByRankWithinTag(items, 'T')[0];
  const topG = sortByRankWithinTag(items, 'G')[0];
  return {
    a1: topA?.id,
    t2: topT?.id,
    g3: topG?.id,
  };
}

export function mergeImportedItems(existing: BootItem[], incoming: BootItem[]): BootItem[] {
  const byRef = new Map(existing.filter((i) => i.sourceRef).map((i) => [i.sourceRef!, i]));
  const merged = [...existing];

  for (const item of incoming) {
    if (item.sourceRef && byRef.has(item.sourceRef)) continue;
    const duplicateText = merged.some(
      (e) => e.text.toLowerCase().trim() === item.text.toLowerCase().trim()
    );
    if (duplicateText) continue;
    merged.push(item);
  }

  return merged;
}

export function createItemId(): string {
  return `boot-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}
